import { useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { CfoActionChoice } from "@/api/types";

function fold(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

/** Searchable pick among the countries the CFO policy knows, plus
 *  "not a place". Only offered values can be chosen: the backend
 *  refuses anything else. */
export function CfoCountryPicker({
  choices,
  value,
  onChange,
  disabled,
}: {
  choices: CfoActionChoice[];
  value: string | null;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const selected = choices.find((c) => c.value === value) ?? null;
  const filtered = useMemo(() => {
    const q = fold(search.trim());
    if (!q) return choices;
    // Any of its names, accents ignored ("etats-unis" finds the US), or
    // its exact code.
    return choices.filter(
      (c) =>
        [c.label, ...(c.aliases ?? [])].some((name) => fold(name).includes(q)) ||
        c.value.toLowerCase() === q,
    );
  }, [choices, search]);

  function pick(next: string) {
    onChange(next);
    setOpen(false);
    setSearch("");
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        if (disabled) return;
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label="Country"
          className="group flex h-9 w-full min-w-0 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-left text-sm shadow-sm transition-colors hover:border-slate-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span
            className={cn("min-w-0 flex-1 truncate", !selected && "text-slate-400")}
          >
            {selected ? selected.label : "Choose a country…"}
          </span>
          <ChevronsUpDown className="h-3 w-3 shrink-0 text-slate-400 group-hover:text-slate-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[300px] p-2"
        align="start"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <Input
          ref={inputRef}
          placeholder="Search a country or code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-2 h-8 text-sm"
          onKeyDown={(e) => {
            if (e.key === "Enter" && filtered.length === 1) {
              e.preventDefault();
              pick(filtered[0].value);
            }
          }}
        />
        <div role="listbox" className="max-h-[240px] space-y-0.5 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-2 py-1.5 text-sm text-slate-400">No match</div>
          )}
          {filtered.map((choice) => (
            <button
              key={choice.value}
              type="button"
              role="option"
              aria-selected={choice.value === value}
              onClick={() => pick(choice.value)}
              className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <span className="truncate">{choice.label}</span>
              {choice.value === value && (
                <Check className="h-3.5 w-3.5 shrink-0 text-indigo-600" />
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
